<template>
  <DeepDialog
    data-test-id="uploads-dialog"
    icon="far fa-upload"
    :max-width="500"
    :title="t('nodes.upload_card.title')"
    v-bind="$attrs"
  >
    <template v-if="taskUpload" #content>
      <div
        v-if="viewsFiltered.length > 1"
        class="d-flex align-center justify-space-between"
      >
        <div class="text-body-small text-grey flex-shrink-0">
          <v-icon class="me-1" size="14">far fa-info-circle</v-icon>
          {{ t('hints.n_folders', totalFolders) }} &
          {{ t('hints.n_files', totalFiles) }}
        </div>
        <div>
          <v-sheet
            class="d-flex align-center justify-end ga-2 px-1"
            color="grey-lighten-2"
            height="40"
            rounded="lg"
          >
            <v-btn
              v-for="item in viewsFiltered"
              :key="item.value"
              class="text-none"
              :icon="item.icon"
              :ripple="false"
              rounded="lg"
              size="x-small"
              :title="item.text"
              :variant="currentView === item.value ? 'flat' : 'plain'"
              @click="currentView = item.value"
            />
          </v-sheet>
        </div>
      </div>

      <div class="my-4">
        <TaskUploadsViewTreeview
          v-if="canShowViewTree && currentView === 'tree'"
          :task-upload="taskUpload"
        />

        <TaskUploadsViewList
          v-else-if="currentView === 'list'"
          :task-upload="taskUpload"
        />
      </div>
    </template>
  </DeepDialog>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { DeepDialog } from '@deepcloud/deep-ui-lib'
import TaskUploadsViewList from '@/components/upload/components/TaskUploadsViewList.vue'
import TaskUploadsViewTreeview from '@/components/upload/components/TaskUploadsViewTreeview.vue'
import type { TaskUpload } from '../types'

const props = defineProps({
  taskUpload: {
    type: Object as PropType<TaskUpload>,
    default: () => ({}),
  },
})

const { t } = useI18n()

const totalFolders = computed(
  () => Object.keys(props.taskUpload.folders ?? {}).length,
)
const totalFiles = computed(() => (props.taskUpload.files ?? []).length)

const canShowViewTree = computed(() => totalFolders.value > 0)

const views = computed(
  () =>
    [
      { text: 'List', icon: 'far fa-list', value: 'list' },
      {
        text: 'Tree',
        icon: 'far fa-list-tree',
        value: 'tree',
        show: () => canShowViewTree.value,
      },
    ] as const,
)

type View = (typeof views.value)[number]['value']

const viewsFiltered = computed(() =>
  views.value.filter((i) => !('show' in i) || i.show()),
)

const currentView = ref<View>(viewsFiltered.value[0].value)

watch(
  () => totalFolders.value,
  (newValue) => {
    if (newValue === 0) {
      currentView.value = 'list'
    }
  },
  { immediate: true },
)
</script>
